import qs from "qs";
import axios from "axios";
import { API_URL, URL } from "../../../../constants/Constants";

const parseJsonApiResponse = (responseData, contextLabel) => {
  if (typeof responseData === "string") {
    try {
      return JSON.parse(responseData);
    } catch (parseError) {
      console.error(`Error al parsear respuesta ${contextLabel}:`, parseError);
      return {
        status: "error",
        message: "La respuesta del servidor no es JSON valido.",
      };
    }
  }

  return responseData || {
    status: "error",
    message: "Respuesta vacia del servidor.",
  };
};

const buildApiErrorResponse = (error, fallbackMessage, contextLabel) => {
  console.error(contextLabel, error);

  const responseData = error?.response?.data;
  const parsedResponseData =
    typeof responseData === "string"
      ? (() => {
          try {
            return JSON.parse(responseData);
          } catch (parseError) {
            return { raw: responseData };
          }
        })()
      : responseData;

  return {
    status: "error",
    statusCode: error?.response?.status ?? null,
    message:
      parsedResponseData?.message ||
      parsedResponseData?.data?.message ||
      parsedResponseData?.error ||
      error?.message ||
      fallbackMessage,
    data: parsedResponseData ?? null,
  };
};

/**
 * Obtiene el estado de entrega de fornituras
 * @param {string} codeScan - Código de la pieza
 * @param {string} type - Tipo de consulta
 * @param {string} status - Estado
 * @param {string} po - Purchase Order
 * @returns {Promise<Object|false>} Datos de estado o false si hay error
 */
export const getStatusFornitureDelivery = async (codeScan, type, status, po) => {
  const queryData = {
    code: codeScan,
    query_type: type,
    status: status,
    po: po,
  };

  try {
    const response = await axios.post(URL, qs.stringify(queryData));
    if (response.data.status === "ok") {
      return response.data.data;
    } else {
      console.log("Pieza no encontrada");
      return false;
    }
  } catch (error) {
    console.error("Error al enviar la conexión POST:", error);
    return false;
  }
};

/**
 * Stock de fornituras (API index.php: screen stockFornituras).
 * @returns {Promise<Array|false>} Filas consolidadas { codigo, unidades, sto_1990, sto_1987, Stock } o false si hay error
 */
export const getStockFornituras = async () => {
  const queryData = {
    screen: "stockFornituras",
    action: "query",
    requestType: "stockFornituras",
  };

  try {
    const response = await axios.post(API_URL, qs.stringify(queryData));
    let responseData = response.data;

    if (typeof responseData === "string" && responseData.includes("{")) {
      try {
        const jsonStart = responseData.indexOf("{");
        responseData = JSON.parse(responseData.substring(jsonStart));
      } catch (parseError) {
        console.error("Error al parsear JSON de stock fornituras:", parseError);
        return false;
      }
    }

    if (responseData.status === "ok" || responseData.status === "success") {
      return Array.isArray(responseData.data) ? responseData.data : [];
    }
    console.log("Error stock fornituras:", responseData.message);
    return false;
  } catch (error) {
    console.error("Error al consultar stock fornituras:", error);
    return false;
  }
};

/**
 * Crea un pedido de fornituras en la API index.php.
 * @param {{ usuario: string, items: Array }} payload
 * @returns {Promise<Object>} Respuesta de la API
 */
export const createForniturasPedido = async (payload) => {
  const body = {
    screen: "forniturasPedidos",
    action: "create",
    usuario: String(payload?.usuario ?? "").trim(),
    items: Array.isArray(payload?.items)
      ? payload.items.map((item) => ({
          codigo: String(item?.codigo ?? "").trim(),
          cantidad: Number(item?.cantidad) || 0,
          unidad: String(item?.unidad ?? "").trim(),
          nombre: String(item?.nombre ?? "").trim(),
          referenciaprov: String(item?.referenciaprov ?? "").trim(),
          nota: String(item?.nota ?? "").trim(),
        }))
      : [],
  };

  try {
    const response = await axios.post(API_URL, body, {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: false,
    });

    return parseJsonApiResponse(response?.data, "createForniturasPedido");
  } catch (error) {
    return buildApiErrorResponse(
      error,
      "Error al enviar el pedido de fornituras.",
      "Error al crear pedido de fornituras:"
    );
  }
};

/**
 * Lista pedidos de fornituras agrupados.
 * @returns {Promise<Object>} Respuesta de la API
 */
export const listForniturasPedidos = async () => {
  const body = {
    screen: "forniturasPedidos",
    action: "list",
  };

  try {
    const response = await axios.post(API_URL, body, {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: false,
    });

    return parseJsonApiResponse(response?.data, "listForniturasPedidos");
  } catch (error) {
    return buildApiErrorResponse(
      error,
      "Error al obtener el historico de pedidos.",
      "Error al listar pedidos de fornituras:"
    );
  }
};

/**
 * Obtiene el detalle de un pedido de fornituras.
 * @param {{ pedido_id?: number|string, batch_uid?: string }} payload
 * @returns {Promise<Object>} Respuesta de la API
 */
export const getForniturasPedidoDetail = async (payload) => {
  const body = {
    screen: "forniturasPedidos",
    action: "detail",
  };

  if (payload?.pedido_id != null && String(payload.pedido_id).trim() !== "") {
    body.pedido_id = Number(payload.pedido_id);
  }
  if (payload?.batch_uid != null && String(payload.batch_uid).trim() !== "") {
    body.batch_uid = String(payload.batch_uid).trim();
  }

  try {
    const response = await axios.post(API_URL, body, {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: false,
    });

    return parseJsonApiResponse(response?.data, "getForniturasPedidoDetail");
  } catch (error) {
    return buildApiErrorResponse(
      error,
      "Error al obtener el detalle del pedido.",
      "Error al obtener detalle de pedido de fornituras:"
    );
  }
};

/**
 * Confirma la recepción de un pedido de fornituras por batch UID.
 * @param {{ batch_uid: string }} payload
 * @returns {Promise<Object>} Respuesta de la API
 */
export const confirmReceiveForniturasPedido = async (payload) => {
  const body = {
    screen: "forniturasPedidos",
    action: "confirmReceive",
    batch_uid: String(payload?.batch_uid ?? "").trim(),
  };

  try {
    const response = await axios.post(API_URL, body, {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: false,
    });

    return parseJsonApiResponse(response?.data, "confirmReceiveForniturasPedido");
  } catch (error) {
    return buildApiErrorResponse(
      error,
      "Error al confirmar la recepción del pedido.",
      "Error al confirmar recepción de pedido de fornituras:"
    );
  }
};

/**
 * Confirma la recepción de un proveedor concreto dentro de un pedido de fornituras.
 * @param {{ batch_uid: string, proveedor: string }} payload
 * @returns {Promise<Object>} Respuesta de la API
 */
export const confirmReceiveForniturasPedidoProveedor = async (payload) => {
  const body = {
    screen: "forniturasPedidos",
    action: "confirmReceiveProveedor",
    batch_uid: String(payload?.batch_uid ?? "").trim(),
    proveedor: String(payload?.proveedor ?? "").trim(),
  };

  try {
    const response = await axios.post(API_URL, body, {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: false,
    });

    return parseJsonApiResponse(response?.data, "confirmReceiveForniturasPedidoProveedor");
  } catch (error) {
    return buildApiErrorResponse(
      error,
      "Error al confirmar la recepción del proveedor.",
      "Error al confirmar recepción de proveedor de fornituras:"
    );
  }
};

/**
 * Confirma la recepción de una línea concreta dentro de un pedido de fornituras.
 * @param {{ batch_uid: string, linea_id: number|string, cantidad_recibida?: number|string, usuario?: string, nota?: string }} payload
 * @returns {Promise<Object>} Respuesta de la API
 */
export const confirmReceiveForniturasPedidoLinea = async (payload) => {
  const body = {
    screen: "forniturasPedidos",
    action: "confirmReceiveLinea",
    batch_uid: String(payload?.batch_uid ?? "").trim(),
    linea_id: Number(payload?.linea_id) || 0,
  };

  const cantidadRecibida = Number(payload?.cantidad_recibida);
  if (Number.isFinite(cantidadRecibida) && cantidadRecibida > 0) {
    body.cantidad_recibida = cantidadRecibida;
  }

  const usuario = String(payload?.usuario ?? "").trim();
  if (usuario) {
    body.usuario = usuario;
  }

  const nota = String(payload?.nota ?? "").trim();
  if (nota) {
    body.nota = nota;
  }

  try {
    const response = await axios.post(API_URL, body, {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: false,
    });

    return parseJsonApiResponse(response?.data, "confirmReceiveForniturasPedidoLinea");
  } catch (error) {
    return buildApiErrorResponse(
      error,
      "Error al confirmar la recepción de la línea.",
      "Error al confirmar recepción de línea de fornituras:"
    );
  }
};

/**
 * Actualiza la cantidad pedida de una línea concreta dentro de un pedido de fornituras.
 * @param {{ batch_uid: string, linea_id: number|string, cantidad: number|string }} payload
 * @returns {Promise<Object>} Respuesta de la API
 */
export const updateForniturasPedidoLineaCantidad = async (payload) => {
  const body = {
    screen: "forniturasPedidos",
    action: "updateLineaCantidad",
    batch_uid: String(payload?.batch_uid ?? "").trim(),
    linea_id: Number(payload?.linea_id) || 0,
    cantidad: Number(payload?.cantidad) || 0,
  };

  try {
    const response = await axios.post(API_URL, body, {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: false,
    });

    return parseJsonApiResponse(response?.data, "updateForniturasPedidoLineaCantidad");
  } catch (error) {
    return buildApiErrorResponse(
      error,
      "Error al actualizar la cantidad de la línea.",
      "Error al actualizar cantidad de línea de fornituras:"
    );
  }
};
